import { refreshToken } from '@/services/system/login';
/**
 * Cookie操作工具集（TypeScript版本）
 */
export function getCookie(name: string): string | null {
  const cookies = document.cookie.split('; ');
  const cookie = cookies.find((row) => row.startsWith(`${name}=`));
  return cookie ? decodeURIComponent(cookie.split('=')[1]) : null;
}

export function setCookie(name: string, value: string, days: number = 7): void {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${date.toUTCString()}; path=/`;
}
export function deleteCookie(name: string): void {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
}

export function getToken() {
  return localStorage.getItem('access_token');
}

export function setToken(token: string) {
  localStorage.setItem('access_token', token);
}

export function clearToken() {
  localStorage.removeItem('access_token');
}

export function formattedDateFun(date: any) {
  const _date = new Date(date);
  const formattedDate = _date
    .toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })
    .replace(/\//g, '-');
  return formattedDate;
}

// 计算宽高比的函数
export function getAspectRatio(width: number, height: number) {
  const gcd = (a: number, b: number): number => {
    return b === 0 ? a : gcd(b, a % b);
  };

  const divisor = gcd(width, height);
  let widthRatio = width / divisor;
  let heightRatio = height / divisor;

  const tolerance = 0.05;

  const standardRatios = [
    [16, 9],
    [9, 16],
    [4, 3],
    [3, 4],
    [1, 1],
    [3, 2],
    [2, 3],
  ];

  for (const [standardWidth, standardHeight] of standardRatios) {
    const calculatedRatio = widthRatio / heightRatio;
    const standardRatio = standardWidth / standardHeight;

    if (Math.abs(calculatedRatio - standardRatio) < tolerance) {
      return `${standardWidth}:${standardHeight}`;
    }
  }

  return `${widthRatio}:${heightRatio}`;
}
export async function generateMaskFromManualCanvas(canvasMask: string) {
  // Create a new canvas with the same dimensions as the mask
  const scribbleCanvas = document.createElement('canvas');
  const img = new Image();
  img.src = canvasMask;

  await new Promise((resolve) => {
    img.onload = () => {
      resolve(true);
    };
  });

  scribbleCanvas.width = img.width;
  scribbleCanvas.height = img.height;
  const scribbleCtx = scribbleCanvas.getContext('2d');
  if (!scribbleCtx) return;

  // Draw the mask onto the canvas
  scribbleCtx.drawImage(img, 0, 0);

  // Get the canvas content
  const imageData = scribbleCtx.getImageData(0, 0, scribbleCanvas.width, scribbleCanvas.height);
  const data = imageData.data;

  // Update pixels: transparent becomes black, the rest become white
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];

    if (a === 0) {
      // Transparent pixel, set to black
      data[i] = 0; // R
      data[i + 1] = 0; // G
      data[i + 2] = 0; // B
      data[i + 3] = 255; // A
    } else {
      // Non-transparent pixel, set to white
      data[i] = 255; // R
      data[i + 1] = 255; // G
      data[i + 2] = 255; // B
      data[i + 3] = 255; // A
    }
  }

  // Draw the modified image data back onto the canvas
  scribbleCtx.putImageData(imageData, 0, 0);

  // Convert the canvas content to a data URL
  const base64data = scribbleCanvas.toDataURL('image/png');
  return base64data;
}
export function formatSize(bytes: any): string {
  if (bytes === 0) return '0 B';
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return (bytes / Math.pow(1024, i)).toFixed(2) + ' ' + sizes[i];
}

export function getFormattedTime() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hour = String(now.getHours()).padStart(2, '0');
  const minute = String(now.getMinutes()).padStart(2, '0');
  const second = String(now.getSeconds()).padStart(2, '0');
  const millisecond = String(now.getMilliseconds()).padStart(3, '0');

  return `${year}_${month}_${day}_${hour}_${minute}_${second}_${millisecond}`;
}

export const AUTO_SEGMENT_SEPARATORS = [',', '\n', '\r'];

export function autoSegmentInputValue(value: any) {
  const splitValue = (item: any) => {
    if (typeof item !== 'string') return [item];
    return item
      .split(/[,\r\n]+/)
      .map((text) => text.trim())
      .filter(Boolean);
  };

  if (Array.isArray(value)) {
    return Array.from(new Set(value.flatMap(splitValue)));
  }

  if (typeof value === 'string') {
    return splitValue(value);
  }

  return value;
}

// 将 SVG 字符串转换为 data URL；已是可展示地址时原样返回
export function svgToDataUrl(svgString: string): string {
  if (!svgString || !svgString.trim()) return '';
  const cleanedSvg = svgString.trim();
  if (/^(https?:)?\/\//i.test(cleanedSvg) || cleanedSvg.startsWith('data:') || cleanedSvg.startsWith('/')) {
    return cleanedSvg;
  }
  const encodedSvg = encodeURIComponent(cleanedSvg);
  return `data:image/svg+xml;charset=utf-8,${encodedSvg}`;
}

export async function refreshTokenFun(): Promise<boolean> {
  try {
    const res = await refreshToken({ skipErrorHandler: true });
    if (res.code === 200 && res.data?.access_token) {
      setToken(res.data.access_token);
      return true;
    }
  } catch (error) {
    // Handled by caller.
  }

  clearToken();
  window.location.href = '/user/login';
  return false;
}

const DOWNLOAD_MIME_BY_EXT: Record<string, string> = {
  html: 'text/html',
  htm: 'text/html',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  zip: 'application/zip',
};

const DOWNLOAD_EXT_BY_MIME: Record<string, string> = {
  'text/html': 'html',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/zip': 'zip',
};

const DOWNLOAD_EXT_BY_FORMAT: Record<string, string> = {
  HTML: 'html',
  DOCX: 'docx',
  XLSX: 'xlsx',
  ZIP: 'zip',
};

export type DownloadByAnchorOptions = {
  /** 期望文件名（可无后缀）；会清洗非法字符并补齐后缀 */
  fileName?: string;
  /** 如 HTML / DOCX / html / docx */
  format?: string;
  /** 接口返回的 content_type */
  contentType?: string;
};

function normalizeDownloadExt(value?: string | null): string | undefined {
  if (!value) {
    return undefined;
  }
  const raw = String(value).trim();
  if (!raw) {
    return undefined;
  }
  const fromFormat = DOWNLOAD_EXT_BY_FORMAT[raw.toUpperCase()];
  if (fromFormat) {
    return fromFormat;
  }
  const withoutDot = raw.replace(/^\./, '').toLowerCase();
  if (DOWNLOAD_MIME_BY_EXT[withoutDot]) {
    return withoutDot;
  }
  return undefined;
}

function extFromContentType(contentType?: string | null): string | undefined {
  if (!contentType) {
    return undefined;
  }
  const mime = contentType.split(';')[0]?.trim().toLowerCase();
  return mime ? DOWNLOAD_EXT_BY_MIME[mime] : undefined;
}

function extFromFileName(fileName?: string | null): string | undefined {
  if (!fileName) {
    return undefined;
  }
  const match = /\.([a-z0-9]+)$/i.exec(fileName.trim());
  return match ? normalizeDownloadExt(match[1]) : undefined;
}

/** 去掉路径非法字符，避免 Chrome 落成「未确认 xxx.crdownload」 */
function sanitizeDownloadBaseName(name: string): string {
  const cleaned = name
    .replace(/[\\/:*?"<>|\u0000-\u001f]/g, '_')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\.+/, '');
  return cleaned || 'download';
}

function stripKnownExtension(fileName: string): string {
  return fileName.replace(/\.(html?|docx|xlsx|zip)$/i, '');
}

function resolveDownloadFileName(url: string, options: DownloadByAnchorOptions = {}): { fileName: string; mimeType?: string } {
  const ext = normalizeDownloadExt(options.format) || extFromContentType(options.contentType) || extFromFileName(options.fileName) || undefined;

  let baseName = options.fileName?.trim();
  if (!baseName) {
    try {
      const pathname = new URL(url, window.location.href).pathname;
      const segment = pathname.split('/').filter(Boolean).pop();
      if (segment) {
        baseName = decodeURIComponent(segment);
      }
    } catch {
      // ignore
    }
  }
  baseName = sanitizeDownloadBaseName(stripKnownExtension(baseName || 'download'));
  const fileName = ext ? `${baseName}.${ext}` : baseName;
  const mimeType = options.contentType?.split(';')[0]?.trim() || (ext ? DOWNLOAD_MIME_BY_EXT[ext] : undefined);
  return { fileName, mimeType };
}

/**
 * 通过 fetch → Blob → ObjectURL + a[download] 触发下载。
 * 跨域资源需目标服务允许 CORS；存储 URL 无后缀时请传 format / contentType。
 */
export async function downloadByAnchor(url: string, fileNameOrOptions?: string | DownloadByAnchorOptions): Promise<void> {
  const options: DownloadByAnchorOptions = typeof fileNameOrOptions === 'string' ? { fileName: fileNameOrOptions } : fileNameOrOptions || {};
  const { fileName, mimeType } = resolveDownloadFileName(url, options);

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Download failed: ${response.status}`);
  }
  const rawBlob = await response.blob();
  const blob = mimeType && rawBlob.type !== mimeType ? new Blob([rawBlob], { type: mimeType }) : rawBlob;
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
}

export const obtainCurrentDomain = (data: { archives?: Array<any> }) => {
  const archives = data.archives ?? [];

  if (archives.length === 0 || archives.every((item: any) => !item?.last_used_at)) {
    return { brand: '', id: '', url: undefined, last_used_at: undefined };
  }

  const latestArchive = archives.reduce((latest, current) => {
    const latestTime = Number(latest?.last_used_at ?? 0);
    const currentTime = Number(current?.last_used_at ?? 0);
    return currentTime > latestTime ? current : latest;
  });

  return latestArchive;
};

/** 品牌档案变更后：当前站点仍有效则保留，否则按 last_used_at / 首项回退 */
export function resolveArchiveSiteUrl(archives: Array<{ url?: string; last_used_at?: number | null }>, currentUrl?: string): string {
  const valid = archives.filter((item): item is { url: string; last_used_at?: number | null } => Boolean(item?.url));
  if (currentUrl && valid.some((item) => item.url === currentUrl)) {
    return currentUrl;
  }
  if (valid.length === 0) {
    return '';
  }
  if (valid.every((item) => !item.last_used_at)) {
    return valid[0].url;
  }
  const latestArchive = valid.reduce((latest, current) => {
    const latestTime = Number(latest.last_used_at ?? 0);
    const currentTime = Number(current.last_used_at ?? 0);
    return currentTime > latestTime ? current : latest;
  });
  return latestArchive.url;
}
